import React, { useEffect, useState } from 'react'
import { Alert, Box, Button, Chip, Container, Divider, FormControlLabel, Grid2, Paper, Stack, Switch, TextField, Typography } from '@mui/material'
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded'
import AccountTreeRoundedIcon from '@mui/icons-material/AccountTreeRounded'
import SaveRoundedIcon from '@mui/icons-material/SaveRounded'
import Navbar from '../../setup/Navbar'
import { useLocation, useNavigate } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { addNewBranch, getBranchData } from '../../../features/AdminManagement/branchSlice'

const emptyForm = { branchID: null, branchName: '', branchAddress: '', branchContactNo: '', branchStatus: true }

export default function BranchesForm() {
    const location = useLocation()
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const branchID = location.state?.branchID || null
    const [formData, setFormData] = useState({ ...emptyForm, branchID })
    const [loading, setLoading] = useState(Boolean(branchID))
    const [saving, setSaving] = useState(false)
    const [error, setError] = useState('')

    useEffect(() => {
        if (!branchID) return
        setLoading(true)
        dispatch(getBranchData({ branchID })).unwrap().then((data) => {
            setFormData({
                branchID, branchName: data.branchName || '', branchAddress: data.branchAddress || '',
                branchContactNo: data.branchContactNo || '', branchStatus: Boolean(data.branchStatus)
            })
        }).catch((err) => setError(err?.message || err || 'Unable to load branch')).finally(() => setLoading(false))
    }, [branchID, dispatch])

    const handleChange = (event) => {
        const { name, value, checked, type } = event.target
        setFormData((current) => ({ ...current, [name]: type === 'checkbox' ? checked : value }))
    }

    const handleSubmit = async (event) => {
        event.preventDefault()
        if (!formData.branchName.trim()) return setError('Branch name is required')
        setSaving(true); setError('')
        try {
            await dispatch(addNewBranch(formData)).unwrap()
            navigate('/Branches')
        } catch (err) {
            setError(err?.message || err || 'Unable to save branch')
        } finally { setSaving(false) }
    }

    return <>
        <Navbar />
        <Box sx={{ minHeight: 'calc(100vh - 64px)', bgcolor: 'var(--web-theme-page-bg, #F5F7FB)', py: { xs: 2, md: 4 } }}>
            <Container maxWidth="md">
                <Paper elevation={0} sx={{ mb: 3, p: { xs: 2.25, md: 3 }, borderRadius: 'var(--web-theme-radius, 12px)', color: 'var(--web-theme-topbar-text, #fff)', background: 'linear-gradient(135deg, var(--web-theme-topbar, #780202), var(--web-theme-primary, #A80707))' }}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={2}>
                        <Stack direction="row" spacing={2} alignItems="center"><Box sx={{ width: 50, height: 50, borderRadius: 2.5, display: 'grid', placeItems: 'center', bgcolor: 'rgba(255,255,255,.15)' }}><AccountTreeRoundedIcon /></Box><Box><Typography variant="h4" fontWeight={800}>{branchID ? 'Edit Branch' : 'Add New Branch'}</Typography><Typography sx={{ opacity: .82 }}>{branchID ? 'Update branch contact and status information' : 'Create a new business location'}</Typography></Box></Stack>
                        {branchID && <Chip label={`Branch #${branchID}`} sx={{ bgcolor: 'rgba(255,255,255,.16)', color: 'inherit', fontWeight: 700 }} />}
                    </Stack>
                </Paper>

                {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{String(error)}</Alert>}
                <Paper component="form" onSubmit={handleSubmit} elevation={0} variant="outlined" sx={{ p: { xs: 2.25, md: 3.5 }, borderRadius: 'var(--web-theme-radius, 12px)', bgcolor: 'var(--web-theme-card-bg, #fff)', opacity: loading ? .65 : 1, pointerEvents: loading ? 'none' : 'auto' }}>
                    <Typography variant="h6" fontWeight={800}>Branch Information</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: .5, mb: 2.5 }}>Enter the location's name and contact details.</Typography>
                    <Divider sx={{ mb: 3 }} />
                    <Grid2 container spacing={2.5}>
                        <Grid2 size={{ xs: 12, md: 8 }}><TextField autoFocus required fullWidth label="Branch Name" name="branchName" value={formData.branchName} onChange={handleChange} inputProps={{ maxLength: 150 }} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} /></Grid2>
                        <Grid2 size={{ xs: 12, md: 4 }}><TextField fullWidth label="Contact No" name="branchContactNo" value={formData.branchContactNo} onChange={handleChange} inputProps={{ maxLength: 30 }} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} /></Grid2>
                        <Grid2 size={12}><TextField fullWidth multiline minRows={3} label="Branch Address" name="branchAddress" value={formData.branchAddress} onChange={handleChange} inputProps={{ maxLength: 500 }} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} /></Grid2>
                        <Grid2 size={12}><Paper variant="outlined" sx={{ px: 2, py: 1, borderRadius: 2, bgcolor: 'rgba(248,250,252,.7)' }}><FormControlLabel control={<Switch checked={formData.branchStatus} onChange={handleChange} name="branchStatus" />} label={formData.branchStatus ? 'Branch is Active' : 'Branch is Inactive'} /></Paper></Grid2>
                    </Grid2>
                    <Stack direction={{ xs: 'column-reverse', sm: 'row' }} justifyContent="flex-end" spacing={1.25} sx={{ mt: 3 }}><Button variant="outlined" startIcon={<ArrowBackRoundedIcon />} onClick={() => navigate('/Branches')}>Back to Branches</Button><Button type="submit" size="large" variant="contained" startIcon={<SaveRoundedIcon />} disabled={saving}>{saving ? 'Saving...' : branchID ? 'Update Branch' : 'Save Branch'}</Button></Stack>
                </Paper>
            </Container>
        </Box>
    </>
}